import React, { useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import extractMediaUrl, { urlRegex } from '../utils/extractMediaUrl';

import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const UniversalMediaRenderer = ({ entry }) => {
    const { t } = useTranslation();
    const { setMediaToView } = useAppContext();
    const [isLoaded, setIsLoaded] = useState(false);
    const [isBlurred, setIsBlurred] = useState(entry.sensitive);

    const handleView = () => {
        setIsBlurred(false);
    };

    const mediaContent = useMemo(() => {
        if (isBlurred) {
            return (
                <div className="relative mt-3 media-container filter blur-lg cursor-pointer" onClick={handleView}>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <p className="text-white font-bold">{t('universalMediaRenderer.sensitiveContent')}</p>
                    </div>
                </div>
            );
        }

        if (!entry || (!entry.mediaUrl && !entry.mediaUrls)) {
            return null;
        }

        if (entry.mediaUrls && entry.mediaUrls.length > 1) {
            return (
                <div className="mt-3 rounded-lg overflow-hidden bg-black/20">
                    <Carousel showThumbs={false} showStatus={false} infiniteLoop useKeyboardArrows>
                        {entry.mediaUrls.map((url, index) => (
                            <div key={index} className="flex justify-center items-center" style={{ maxHeight: '400px' }}>
                                <img
                                    src={url}
                                    alt={`${entry.content || t('universalMediaRenderer.altText')} ${index + 1}`}
                                    className="max-w-full max-h-[400px] object-contain"
                                    onClick={() => setMediaToView(url)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </div>
                        ))}
                    </Carousel>
                </div>
            );
        }

        const mediaUrl = entry.mediaUrl || (entry.mediaUrls && entry.mediaUrls[0]);

        if (!mediaUrl) return null;

        const isVideo = mediaUrl.match(/\.(mp4|webm|ogg|mov)$/i) || ReactPlayer.canPlay(mediaUrl);
        const isAudio = mediaUrl.match(/\.(mp3|wav|ogg|webm)$/i);

        if (isAudio) {
            return (
                <div className={`relative mt-3 media-container transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} >
                    <audio controls src={mediaUrl} onCanPlay={() => setIsLoaded(true)} className="w-full" />
                </div>
            );
        }

        if (isVideo) {
            return (
                <div className={`relative mt-3 media-container transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} >
                    <ReactPlayer
                        url={mediaUrl}
                        controls={true}
                        width="100%"
                        height="100%"
                        onReady={() => setIsLoaded(true)}
                    />
                </div>
            );
        } else {
            return (
                <div className="mt-3 bg-black/20 rounded-lg flex justify-center items-center overflow-hidden w-full" style={{ maxHeight: '400px' }}>
                    <img
                        src={mediaUrl}
                        alt={entry.content || t('universalMediaRenderer.altText')}
                        className={`relative transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} max-w-full max-h-[400px] object-contain w-full h-auto`}
                        onLoad={() => setIsLoaded(true)}
                        onClick={() => setMediaToView(mediaUrl)}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            );
        }

    }, [entry, setMediaToView, isLoaded, isBlurred, t]);

    if (!mediaContent) {
        if (entry.mediaUrl || (entry.mediaUrls && entry.mediaUrls.length > 0) || extractMediaUrl(entry.content).mediaUrl) {
            return (
                <div className="player-wrapper bg-red-900/20 border border-red-500/50 flex flex-col items-center justify-center text-red-400 mt-3">
                    <LucideIcons.AlertTriangle size={32} />
                    <p className="mt-2 text-sm font-semibold">{t('universalMediaRenderer.loadError')}</p>
                </div>
            );
        }
        return null;
    }

    return mediaContent;
};

export default UniversalMediaRenderer;